/* Common Imports */

import { styled, useMediaQuery, useTheme } from "@mui/system";

/* Redux Imports */

import { ObjectId } from "bson";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";

/* Component Imports */

import ContactDetailsCardDesktopSkeleton from "../../../../skeletons/single-property-page/desktop/ContactDetailsCardDesktopSkeleton";
import PDFCardDesktopSkeleton from "../../../../skeletons/single-property-page/desktop/PDFCardDesktopSkeleton";
import SharePropertyDesktopSkeleton from "../../../../skeletons/single-property-page/desktop/SharePropertyDesktopSkeleton";
import ContactDetailCard from "./ContactDetailCard";
import CopyListing from "./CopyListing";
import ListingStatsSummary from "./ListingStatsSummary";
import ListingPromoteActions from "./ListingPromoteActions";
import PDF from "./DownloadPDF";
import PublishPublicButtons from "./PublishPublicButtons";
import ShareProperty from "./ShareProperty";
import ShowContactDetails from "./ShowContactDetails";

/* Styled Components */

const Container = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	gap: "1rem",
}));

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const RightContainer = ({
	propertyData,
	property_created_by,
	session,
	redux_session_updated,
	dispatch,
	isEditor,
	loading,
	profile_context,
	creatorInnerRef,
	login_flow_global_state,
	screenType,
}: {
	propertyData: LocalPropertyInterface;
	property_created_by: LocalPropertyInterface["created_by"];
	session: any;
	redux_session_updated: boolean;
	dispatch: Function;
	isEditor: boolean;
	loading: boolean;
	profile_context: StateInterface["profile_context"];
	creatorInnerRef: any;
	login_flow_global_state: StateInterface["login_flow_global_state"];
	screenType: string;
}) => {
	const theme = useTheme();

	const smDevice = useMediaQuery(theme.breakpoints.down("sm"));

	const creator_id: ObjectId | string = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id != ""
				? (property_created_by.business_profile_id as BusinessProfileInterface)._id
				: property_created_by.user_id != ""
					? (property_created_by.user_id as UserInterface)._id
					: ""
			: property_created_by.user_id != ""
				? (property_created_by.user_id as UserInterface)._id
				: ""
		: "";

	const creator_user_id: ObjectId | string = property_created_by
		? property_created_by.is_business_profile
			? (property_created_by.user_id as ObjectId)
			: property_created_by.user_id != ""
				? (property_created_by.user_id as UserInterface)._id
				: ""
		: "";

	const profile_context_id: ObjectId | string = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: "";

	const user_id: ObjectId | string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
					? session.user.dbUser._id
					: ""
				: ""
			: ""
		: "";

	const logged_user_access: string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser
					? session.user.dbUser.access_level
						? session.user.dbUser.access_level
						: ""
					: ""
				: ""
			: ""
		: "";

	return (
		<Container ref={creatorInnerRef}>
			{/* Show Contact Details */}

			{propertyData.published ? (
				profile_context_id === creator_id || isEditor ? (
					<ShowContactDetails
						dispatch={dispatch}
						session={session}
						loading={loading}
						propertyData={propertyData}
						property_created_by={property_created_by}
						profile_context={profile_context}
					/>
				) : null
			) : null}

			{/* Contact Details */}
			{loading ? (
				<ContactDetailsCardDesktopSkeleton />
			) : (
				<>
					{propertyData.published ? (
						profile_context_id === creator_id || isEditor ? null : (
							<ContactDetailCard
								property_created_by={property_created_by}
								property_slug={propertyData.slug}
								property_title={propertyData.title}
								property_show_contact_details={propertyData.show_contact_details}
								transaction_type={propertyData.transaction_type}
								dispatch={dispatch}
								session={session}
								loading={loading}
								boost_information={propertyData.boost_information}
								property_id={propertyData._id}
								profile_context={profile_context}
								login_flow_global_state={login_flow_global_state}
								screenType={screenType}
							/>
						)
					) : null}
				</>
			)}

			{/* Download PDF */}
			{loading ? (
				<PDFCardDesktopSkeleton />
			) : (
				<>
					{propertyData.published ? (
						<PDF
							published={propertyData.published}
							loading={loading}
							profile_context={profile_context}
							screenType={screenType}
						/>
					) : null}
				</>
			)}

			{/* Views and leads, for the listing's team only (the route decides) */}

			{loading || !session?.user ? null : <ListingStatsSummary
					property_id={String(propertyData._id || "")}
					owner_business_profile_id={
						property_created_by?.is_business_profile
							? String(
									typeof property_created_by.business_profile_id === "object"
										? (property_created_by.business_profile_id as any)?._id || ""
										: property_created_by.business_profile_id || "",
								)
							: ""
					}
					boost_active={(propertyData as any)?.boost_information?.active === true}
					updated_at={(propertyData as any)?.updated_at ? String((propertyData as any).updated_at) : ""}
				/>}

			{/* Broadcast and Boost, for the listing's team on a published listing (gap A2; the route decides) */}

			{loading || !session?.user || !property_created_by?.is_business_profile ? null : (
				<ListingPromoteActions
					propertyData={propertyData}
					businessProfileId={
						property_created_by?.is_business_profile
							? String(
									typeof property_created_by.business_profile_id === "object"
										? (property_created_by.business_profile_id as any)?._id || ""
										: property_created_by.business_profile_id || "",
								)
							: ""
					}
					dispatch={dispatch}
					session={session}
					profile_context={profile_context}
				/>
			)}

			{/* Copy Listing */}

			<CopyListing
				dispatch={dispatch}
				session={session}
				loading={loading}
				propertyData={propertyData}
				profile_context={profile_context}
				property_created_by={(propertyData as LocalPropertyInterface).created_by}
			/>

			{/* Share */}
			{loading ? (
				<SharePropertyDesktopSkeleton />
			) : (
				<>
					{propertyData.published ? (
						<ShareProperty
							url={process.env.PRODUCTION_URL + "properties/" + propertyData.slug}
							loading={loading}
							user_id={user_id}
							creator_id={creator_user_id}
							dispatch={dispatch}
							property_created_by={property_created_by}
							property_id={propertyData._id}
							profile_context={profile_context}
							screenType={screenType}
						/>
					) : null}
				</>
			)}

			{/* Publish / Unpublish buttons */}

			{creator_id === profile_context_id ||
			isEditor ||
			(logged_user_access.includes("admin") &&
				logged_user_access.includes("property") &&
				propertyData.assigned_to.includes(user_id)) ||
			logged_user_access.includes("super_admin") ? (
				<PublishPublicButtons
					propertyData={propertyData as PropertyInterface}
					session={session}
					dispatch={dispatch}
					loading={loading}
					profile_context={profile_context}
					isEditor={isEditor}
				/>
			) : null}
		</Container>
	);
};

export default RightContainer;
